import { useEffect, useState } from "react";
import { CalendarRange, Check, Loader2 } from "lucide-react";
import { fetchMonthAdvice } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import type { Interest, MonthAdvice, MonthAnswer as Answer, MonthReason, UiLang } from "../lib/types";
import { FoldPanel } from "./FoldPanel";

interface Props {
  lang: UiLang;
  /** Ruta folkemengdene skal gjelde for, når brukeren har valgt en. */
  corridorId?: string;
}

/**
 * «Når bør jeg komme til Norge?»
 *
 * ── Hvorfor panelet står der det står ──────────────────────────────────────
 *
 * Over rutevalget, ikke under. Måneden avgjør ruta mer enn ruta avgjør
 * måneden: den som bare kan reise i desember skal ikke først velge Otta–Flåm
 * og *så* få vite at fjellovergangene er stengt. Spørsmålet kommer derfor før
 * det stedet appen ellers spør «hvor skal du?».
 *
 * ── Hvorfor det ikke finnes en beste måned ─────────────────────────────────
 *
 * Fordi aksene er uenige. Juni–september er de eneste månedene der ingen av de
 * 118 stoppene er stengt, og nettopp de månedene har *ingen* nordlys. Et tall
 * som rangerte månedene ville skjult den motsetningen framfor å vise den, og
 * det er den motsetningen som er svaret.
 *
 * Spørsmålet blir svarbart så snart man sier hva man kommer for. Derfor samme
 * knapperad som ruteforslaget — og ingen månedsvelger, for måneden er det
 * panelet svarer *med*, ikke noe det spør om.
 *
 * ── Hvorfor hver måned både gir og tar ─────────────────────────────────────
 *
 * Fordi ingen av dem bare tar. November er elleve stengte stopp *og* nordlys
 * på ni; skriver man bare det første, er svaret sant og ubrukelig. Det er den
 * samme regelen `WinterPanel` finnes for.
 */

/** Samme rekkefølge som i ruteforslaget, så knappene ikke flytter seg. */
const INTERESTS: Interest[] = [
  "fjord",
  "fjell",
  "by",
  "historie",
  "mat",
  "nordlys",
  "midnattssol",
  "strand",
  "dyreliv",
  "tog",
  "ski",
];

export function MonthAnswer({ lang, corridorId }: Props) {
  const t = dictionaries[lang];
  const [chosen, setChosen] = useState<Interest[]>([]);
  const [advice, setAdvice] = useState<MonthAdvice | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetchMonthAdvice(chosen, lang, corridorId)
      .then((result) => {
        // Et svar som kommer etter et nyere svar skal ikke overskrive det.
        if (live) setAdvice(result);
      })
      .catch(() => {
        // Stille: panelet er opplysning, ikke en handling. En feilmelding her
        // ville stjålet oppmerksomhet fra det brukeren faktisk kom for.
        if (live) setAdvice(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [chosen, lang, corridorId]);

  const toggle = (interest: Interest) =>
    setChosen((current) =>
      current.includes(interest)
        ? current.filter((i) => i !== interest)
        : [...current, interest],
    );

  /** Setningen som står i hodet når panelet er lagt sammen. */
  const summary = () => {
    if (!advice) return t.monthAnswerLead;
    if (chosen.length === 0) return t.monthAnswerLead;
    if (advice.matches.length === 0) return t.monthAnswerNone;
    return t.monthAnswerMatches(advice.matches.map((m) => t.monthName[m - 1]).join(", "));
  };

  /** Én kode blir én setning, med tallene fra svaret satt inn. */
  const line = (reason: MonthReason, month: Answer): string => {
    const f = month.facts;
    switch (reason) {
      case "midnightSun":
        return t.monthGiveMidnightSun;
      case "aurora":
        return t.monthGiveAurora(f.auroraStops);
      case "everythingOpen":
        return t.monthGiveAllOpen;
      case "quiet":
        return t.monthGiveQuiet(f.visitorLoad ?? 0);
      case "longDays":
        return t.monthGiveLongDays(f.daylightNorth);
      case "closures":
        return t.monthTakeClosures(f.closedStops, f.closedExamples.join(", "));
      case "shortDays":
        return t.monthTakeShortDays(f.daylightNorth);
      case "polarNight":
        return t.monthTakePolarNight;
      case "busy":
        return t.monthTakeBusy(f.visitorLoad ?? 0);
      case "noAurora":
        return t.monthTakeNoAurora;
      case "noMidnightSun":
        return t.monthTakeNoMidnightSun;
    }
  };

  return (
    <FoldPanel
      id="monthAnswer"
      title={t.monthAnswerTitle}
      icon={<CalendarRange size={14} />}
      summary={summary()}
    >
      <p className="card-sub">{t.monthAnswerHint}</p>

      <div className="chips" role="group" aria-label={t.interestsLabel}>
        {INTERESTS.map((interest) => {
          const active = chosen.includes(interest);
          return (
            <button
              key={interest}
              type="button"
              className={`chip ${active ? "chip--on" : ""}`}
              aria-pressed={active}
              onClick={() => toggle(interest)}
            >
              {active && <Check size={13} strokeWidth={3} />}
              {t.interestName[interest]}
            </button>
          );
        })}
      </div>

      {/*
        Svaret, også når panelet står åpent.

        Det sto bare i hodet, som `FoldPanel` viser når panelet er lagt sammen —
        altså overalt unntatt der brukeren faktisk leser. «Ingen måned gir alt du
        valgte» er den viktigste setningen i hele panelet: uten den ser tolv
        rader uten et eneste treff ut som at noe er i stykker.
      */}
      {advice && chosen.length > 0 && (
        <p className={advice.matches.length === 0 ? "note note--warn" : "month-verdict"}>
          {advice.matches.length === 0
            ? t.monthAnswerNone
            : t.monthAnswerMatches(
                advice.matches.map((m) => t.monthName[m - 1]).join(", "),
              )}
        </p>
      )}

      {/*
        Det måneden ikke rår over, sagt rett ut.

        Ski, dyreliv, strand og fjell ser sesongbetonte ut og er det ikke —
        Fonna og Galdhøpiggen er sommerskisentre, hvalsafarien utenfor Andenes
        går i desember. Å gi dem en måned ville vært å slette nettopp det den
        som spør er ute etter.
      */}
      {advice && advice.noSeason.length > 0 && (
        <p className="note">
          {t.monthNoSeason(advice.noSeason.map((i) => t.interestName[i]).join(", "))}
        </p>
      )}

      {loading && !advice && (
        <p className="note">
          <Loader2 size={13} className="spin" /> {t.finding}
        </p>
      )}

      {advice && (
        <div className="months">
          {advice.months.map((month) => {
            const matched = advice.matches.includes(month.month);
            return (
              <div
                key={month.month}
                className={`month ${matched && chosen.length > 0 ? "month--match" : ""}`}
              >
                <span className="month-name">{t.monthName[month.month - 1]}</span>
                <ul className="month-gives">
                  {month.gives.map((reason) => (
                    <li key={reason}>{line(reason, month)}</li>
                  ))}
                </ul>
                <ul className="month-takes">
                  {month.takes.map((reason) => (
                    <li key={reason}>{line(reason, month)}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/*
        De faste forbeholdene, som tekst og ikke som en fotnote.

        Regnet er det folk venter at svaret handler om, og det er den svakeste
        aksen i hele datasettet: andelen våte dager ligger mellom 0,44 og 0,58
        hver eneste måned. Sies det ikke, leser man et fravær som et råd.
      */}
      {advice && (
        <div className="month-caveats">
          <p className="note">{t.monthCaveatRain}</p>
          <p className="note">{t.monthCaveatWhere}</p>
          <p className="note">{t.monthCaveatNormals}</p>
        </div>
      )}
    </FoldPanel>
  );
}
